import { useEffect } from 'react';
import {
    GetFormalOffersResponse,
    GetMcaEstimatedScheduleResponse
} from '@iwoca/lapi-client/edge';
import { Input, Spinner } from '@iwoca/orion';
import styles from './rblSweepSelect.module.css';
import classNames from 'classnames';
import messages from './rblSweepSelect.messages';
import { ChooseSweepAmount } from '../../../../../sharedDashboardComponents/slider/chooseSweepAmount/ChooseSweepAmount';
import { convertSweepToPercentage } from '../RBLOfferConfiguration.helpers';
import { useIntl } from 'react-intl';

type TSweepVals = {
    min: number;
    max: number;
    absMax: number;
    absMin: number;
};

type TRBLSweepSelect = {
    offer: NonNullable<
        GetFormalOffersResponse['data']
    >['formal_offers'][number];
    drawdownAmount: number;
    sweep: number;
    setSweep: (sweep: number) => void;
    schedule: NonNullable<GetMcaEstimatedScheduleResponse['data']> | null;
    sweepVals: TSweepVals;
    setSweepVals: React.Dispatch<React.SetStateAction<TSweepVals>>;
};

export const RblSweepSelect = ({
    offer,
    drawdownAmount,
    sweep,
    setSweep,
    schedule,
    sweepVals,
    setSweepVals
}: TRBLSweepSelect) => {
    const intl = useIntl();
    const minAmount = 2200;

    const amountInRange =
        !!drawdownAmount &&
        drawdownAmount >= minAmount &&
        drawdownAmount <= offer.max_amount!;

    const disabled = !amountInRange;

    useEffect(() => {
        const checkSweepInRange = (sweep: number, min: number, max: number) => {
            if (sweep === 0 || !(sweep >= min && sweep <= max)) {
                setSweep(max);
            }
        };

        if (schedule) {
            const newSweepMin = convertSweepToPercentage(
                schedule.summary.sweep.minimum_sweep_fraction
            );
            const newSweepMax = convertSweepToPercentage(
                schedule.summary.sweep.maximum_sweep_fraction
            );

            setSweepVals((prevSweepVals: TSweepVals) => ({
                ...prevSweepVals,
                min: newSweepMin,
                max: newSweepMax
            }));

            checkSweepInRange(sweep, newSweepMin, newSweepMax);
        }
    }, [schedule, setSweepVals, sweep, setSweep]);

    if (!drawdownAmount) {
        return (
            <div
                className={classNames(styles.container)}
                data-testId="spinner-container"
            >
                <Spinner className={styles.spinner} />
            </div>
        );
    }
    return (
        <div className={classNames(styles.container)}>
            <Input
                label={intl.formatMessage(messages.input)}
                iconRight="percent"
                value={sweep}
                type="number"
                step="0.01"
                onChange={(ev) => setSweep(parseFloat(ev.target.value))}
                disabled={disabled}
            />

            <ChooseSweepAmount
                sweep={sweep}
                absSweepMax={sweepVals.absMax}
                absSweepMin={sweepVals.absMin}
                sweepMax={sweepVals.max}
                sweepMin={sweepVals.min}
                updateSweepValue={(value) => setSweep(value)}
            />
        </div>
    );
};
